// services/JwtAuthService.js
import jwt_decode from "jwt-decode";
import localStorageService from "./localStorageService";

// Sliding session timeout in milliseconds (30 minutes of inactivity)
const SESSION_TIMEOUT = 30 * 60 * 1000;

// Grace period to account for client clock skew
const CLOCK_SKEW_MS = 30 * 1000;

// Session bookkeeping only. The network calls (login, register, verify,
// forgot/change password) live in containers/Login/UserFunctions.js,
// SignInForm, services/passwordResetService.js and services/accountService.js
// on the shared http instance; the old fetch-based helpers here (and src/api.js
// they depended on) were unreachable and have been removed.
class JwtAuthService {
  // Check if the JWT itself is expired (server-issued exp claim)
  isTokenExpired = () => {
    const token = localStorage.getItem("token");
    if (!token) return true;
    try {
      const decoded = jwt_decode(token);
      if (!decoded.exp) return false;
      return decoded.exp * 1000 < Date.now() - CLOCK_SKEW_MS;
    } catch (e) {
      return true;
    }
  };

  // Check if the inactivity-based session is still valid
  isSessionActive = () => {
    const sessionStart = localStorage.getItem("session_start");
    if (!sessionStart) return false;
    const elapsed = Date.now() - parseInt(sessionStart, 10);
    return elapsed < SESSION_TIMEOUT;
  };

  // Combined check: must have a valid JWT AND be within the inactivity window
  isSessionValid = () => {
    return !this.isTokenExpired() && this.isSessionActive();
  };

  getSessionTimeRemaining = () => {
    const sessionStart = localStorage.getItem("session_start");
    if (!sessionStart) return 0;
    const elapsed = Date.now() - parseInt(sessionStart, 10);
    const remaining = SESSION_TIMEOUT - elapsed;
    return remaining > 0 ? remaining : 0;
  };

  // Refresh inactivity timer on user activity (debounced from App.js side)
  refreshSession = () => {
    if (!this.isSessionValid()) return;
    localStorage.setItem("session_start", Date.now().toString());
    this.startSessionTimer();
  };

  startSessionTimer = () => {
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
    }

    // Schedule a logout at the earlier of: inactivity timeout OR JWT expiry
    const inactivityRemaining = this.getSessionTimeRemaining();

    let jwtRemaining = Number.POSITIVE_INFINITY;
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwt_decode(token);
        if (decoded.exp) {
          jwtRemaining = decoded.exp * 1000 - Date.now();
        }
      } catch (e) {
        jwtRemaining = 0;
      }
    }

    const fireIn = Math.max(0, Math.min(inactivityRemaining, jwtRemaining));

    this.sessionTimer = setTimeout(() => {
      console.log('[Session] Timeout - logging out');
      this.handleSessionExpired();
    }, fireIn);
  };

  // Centralized expiry handler — emits an event so SessionWatcher can redirect
  handleSessionExpired = () => {
    this.logout();
    // Notify the app (SessionWatcher listens for this)
    window.dispatchEvent(new Event('session-expired'));
  };

  loginWithToken = () => {
    return new Promise((resolve, reject) => {
      const token = localStorage.getItem("token");
      const user = localStorageService.getItem("auth_user");

      if (!token || !user) {
        reject(new Error("No session found"));
        return;
      }

      if (!this.isSessionValid()) {
        this.logout();
        reject(new Error("Session expired"));
        return;
      }

      this.setSession(token);
      this.startSessionTimer();
      resolve(user);
    });
  };

  logout = () => {
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
      this.sessionTimer = null;
    }
    this.setSession(null);
    this.removeUser();
  };

  // Unified key naming: 'token' (matches App.js) and 'authenticated'.
  // The token is attached per-request by services/http.js — never via
  // axios.defaults, which would send it to every third-party host too.
  setSession = (token) => {
    if (token) {
      localStorage.setItem("token", token);
      localStorage.setItem("authenticated", "true");
      localStorage.setItem("session_start", Date.now().toString());
    } else {
      localStorage.removeItem("token");
      localStorage.removeItem("authenticated");
      localStorage.removeItem("session_start");
      localStorage.removeItem("userId");
      localStorage.removeItem("email");
    }
  };

  setUser = (user) => {
    localStorageService.setItem("auth_user", user);
  };

  removeUser = () => {
    localStorage.removeItem("auth_user");
  };
}

export default new JwtAuthService();